import { BusyIndicator, DPFormField, DPInputText, DPInputUserTitleDropdown, FormikForm } from '@framework'
import { useApiClient } from '@hooks/useApiClient'
import { useSession } from '@hooks/useSession'
import { useTranslation } from '@hooks/useTranslation'
import { useState } from 'react'
import { Modal } from 'semantic-ui-react'
import * as Yup from 'yup'
import { UserProfileApiClient } from 'api'
import styles from './UserTitleConfirmationModal.module.css'
import { DPButton } from 'shared'
import { useFormikContext } from 'formik'
import { isFeatureEnabled } from '@config'

interface IForm {
    title: string,
    phone: string,
    email: string,
    smsCode: string
}

export function UserTitleConfirmationModal() {
    const session = useSession()
    const { nextTitleConfirmation, title, phone, email } = session.currentUser
    const $t = useTranslation('UserTitleConfirmationModal')
    const show = nextTitleConfirmation != undefined && new Date(nextTitleConfirmation).getTime() < Date.now()
    const [open, setOpen] = useState(show)
    const [busy, setBusy] = useState(false)
    const [smsCodeSent, setSmsCodeSent] = useState<boolean>(false)
    const api = useApiClient(x => new UserProfileApiClient(x))
    const smsCode = ''
    const [smsCodeId, setSmsCodeId] = useState<number | null>(window.localStorage.getItem('smsCodeId') ? parseInt(window.localStorage.getItem('smsCodeId')!) : null)
    const smsCodeVerificationEnabled = isFeatureEnabled('sms-code-verification')

    const initialValues: IForm = {
        title,
        phone,
        email,
        smsCode,
    }

    let validationSchema = Yup.object().shape({
        title: Yup.string().required($t('ValidationTitleRequired')),
        phone: Yup.string().required($t('ValidationPhoneRequired')).length(8, $t('ValidationPhoneLength')),
        email: Yup.string().required($t('ValidationEmailRequired')).email($t('ValidationEmailValid')),
    })

    if (smsCodeVerificationEnabled) {
        validationSchema = validationSchema.concat(Yup.object().shape({
            smsCode: Yup.string()
                .required($t('ValidationSMSCodeRequired'))
                .test('smsCodeValid', $t('ValidationSMSCodeInvalid'), async (value) => {
                    if (!value) {
                        return true;
                    }

                    return await api.validateSmsCode(value!, smsCodeId!)
                })
        }))
    }

    const onSubmit = async (values: IForm) => {
        setBusy(true)
        try {
            const changeTitleResponse = await api.userPromptUpdate(values.title!, values.phone!, values.email!, values.smsCode!, smsCodeId!)
            if (!changeTitleResponse.success) {
                return
            }

            // Refresh session to fetch new value of next title confirmation
            await session.refresh()
            setOpen(false)
        } finally {
            setBusy(false)
        }
    }

    const sendSmsCode = async (phone: string) => {
        setBusy(true)
        try {
            const codeId = await api.sendSmsCode(phone)
            setSmsCodeId(codeId)
            window.localStorage.setItem('smsCodeId', codeId.toString())
            setSmsCodeSent(true)
        } finally {
            setBusy(false)
        }
    }

    const SendSmsCodeButton = () => {
        const { values } = useFormikContext<IForm>();

        return (
            <DPButton onClick={() => sendSmsCode(values.phone)}>
                {$t('SendCode')}
            </DPButton>
        );
    }

    return (
        <Modal
            open={open}
            size="mini"
        >
            <Modal.Header>
                {$t('TitleConfirmation')}
            </Modal.Header>
            <Modal.Content>
                <BusyIndicator busy={busy}>
                    <p>
                        {$t('InfoMessage')}
                    </p>
                    <FormikForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        validationSchema={validationSchema}
                    >
                        {/* Title */}
                        <DPFormField
                            required
                            name="title"
                            label={$t('Title')}
                            inline={false}
                        >
                            <DPInputUserTitleDropdown
                                name="title"
                            />
                        </DPFormField>

                        {/* Phone number */}
                        <div className={styles['phone-container']}>
                            <DPFormField
                                required
                                name="phone"
                                label={$t('Phone')}
                                inline={false}
                                className={styles['phone-form-field']}
                            >
                                <DPInputText
                                    name="phone"
                                />
                            </DPFormField>
                            {smsCodeVerificationEnabled && (
                                <div className={styles['phone-button']}>
                                    <SendSmsCodeButton />
                                    {smsCodeSent && <small className={styles['sms-sent']}>{$t('CodeSent')}</small>}
                                </div>
                            )}
                        </div>

                        {/* E-mail address */}
                        <DPFormField
                            required
                            name="email"
                            label={$t('Email')}
                            inline={false}
                        >
                            <DPInputText
                                name="email"
                            />
                        </DPFormField>

                        {/* sms code */}
                        {smsCodeVerificationEnabled && (
                            <DPFormField
                                required
                                name="smsCode"
                                label={$t('SMS Code')}
                                inline={false}
                            >
                                <DPInputText
                                    name="smsCode"
                                />
                            </DPFormField>
                        )}

                        {/* Submit */}
                        <div className={styles['text-right']}>
                            <DPButton>
                                {$t('Confirm')}
                            </DPButton>
                        </div>
                    </FormikForm>
                </BusyIndicator>
            </Modal.Content>
        </Modal>
    )
}